import { useState, useRef } from "react";
import { Upload, Loader2, X, Image as ImageIcon } from "lucide-react";
import { uploadImage } from "../../lib/content";
import { toast } from "sonner";

export const ImageUpload = ({ value, onChange, testId = "image-upload" }) => {
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Max 5 MB");
      return;
    }
    setBusy(true);
    try {
      const res = await uploadImage(file);
      onChange(res.absolute_url);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
      if (ref.current) ref.current.value = "";
    }
  };

  return (
    <div className="flex items-start gap-3" data-testid={testId}>
      <div className="w-24 h-24 rounded-lg bg-slate-100 border border-slate-200 overflow-hidden flex items-center justify-center flex-shrink-0">
        {value ? (
          <img src={value} alt="preview" className="w-full h-full object-cover" />
        ) : (
          <ImageIcon className="w-6 h-6 text-slate-400" />
        )}
      </div>
      <div className="flex-1">
        <input
          ref={ref}
          type="file"
          accept="image/png,image/jpeg,image/webp,image/gif"
          onChange={onFile}
          className="hidden"
          id={`${testId}-input`}
        />
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => ref.current?.click()}
            disabled={busy}
            className="inline-flex items-center gap-1.5 px-3 h-9 rounded-md bg-[#0A192F] hover:bg-[#0F2648] text-white text-sm font-semibold disabled:opacity-50"
            data-testid={`${testId}-btn`}
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
            {busy ? "Uploading..." : value ? "Replace" : "Upload"}
          </button>
          {value && (
            <button
              type="button"
              onClick={() => onChange("")}
              className="inline-flex items-center gap-1.5 px-3 h-9 rounded-md bg-slate-100 hover:bg-slate-200 text-[#0A192F] text-sm font-semibold"
              data-testid={`${testId}-clear`}
            >
              <X className="w-3.5 h-3.5" /> Clear
            </button>
          )}
        </div>
        <input
          type="text"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Or paste image URL"
          className="mt-2 w-full text-xs px-2.5 h-8 rounded border border-slate-200 focus:outline-none focus:border-[#00B37E]"
          data-testid={`${testId}-url`}
        />
        <p className="text-[11px] text-slate-500 mt-1">PNG / JPG / WEBP · ≤ 5 MB</p>
      </div>
    </div>
  );
};

export default ImageUpload;
